import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Plane, RefreshCw, Smartphone, Wifi, WifiOff } from 'lucide-react';
import clsx from 'clsx';

import { adbApi } from '@/lib/api';
import { KvCard } from '@/components/KvCard';

interface WifiStatus {
  enabled: boolean;
  ssid?: string;
  bssid?: string;
  rssi?: number;
  linkSpeedMbps?: number;
  frequencyMhz?: number;
  ipAddress?: string;
}

interface AirplaneState {
  on: boolean;
}

/**
 * Top card on the Network page. Three rows: WiFi (with SSID/RSSI readout when
 * connected), mobile data, airplane mode. Each toggle uses optimistic UI:
 * we flip the displayed state immediately, fire `net.<x>.{enable,disable}`,
 * and roll back if the call rejects. After the mutation succeeds we
 * `invalidateQueries` to pull the authoritative state (some devices need a
 * second or two to report the new SSID).
 */
export function ConnectivityCard({ serial }: { serial?: string }) {
  const qc = useQueryClient();
  const enabled = !!serial;

  const wifiQ = useQuery({
    queryKey: ['net.wifi.status', serial],
    queryFn: () => adbApi.run<WifiStatus>({ serial, name: 'net.wifi.status' }),
    enabled,
    staleTime: 5_000,
    // Refetch every 10s while the page is open so the RSSI ticker feels live.
    refetchInterval: enabled ? 10_000 : false,
  });
  const airplaneQ = useQuery({
    queryKey: ['net.airplane.get', serial],
    queryFn: () => adbApi.run<AirplaneState>({ serial, name: 'net.airplane.get' }),
    enabled,
    staleTime: 5_000,
  });

  // Mobile data has no dedicated "get" — the only signal we can derive
  // cheaply is whether `dumpsys connectivity` shows a MOBILE network active.
  // For the toggle UX we trust the user's last action, so we keep the state
  // local-only until they hit the button.
  const refresh = (): void => {
    qc.invalidateQueries({ queryKey: ['net.wifi.status', serial] });
    qc.invalidateQueries({ queryKey: ['net.airplane.get', serial] });
  };

  const wifi = wifiQ.data?.result;
  const airplane = airplaneQ.data?.result;

  // Surfacing query errors prevents the "silent OFF" trap: if the server's
  // catalog doesn't know `net.wifi.status` (e.g. user forgot to restart
  // after upgrading), every toggle would otherwise just sit at OFF with no
  // explanation. We pick the first error to display; once any one query
  // recovers the banner clears.
  const fatalErr =
    (wifiQ.error as Error | undefined) ?? (airplaneQ.error as Error | undefined);
  const [actionErr, setActionErr] = useState<string | null>(null);

  const runToggle = async (cmd: string, optimistic: () => void, rollback: () => void) => {
    if (!serial) return;
    optimistic();
    setActionErr(null);
    try {
      await adbApi.run({ serial, name: cmd });
    } catch (e) {
      rollback();
      setActionErr((e as Error).message);
    } finally {
      // Whatever happened, give it ~600ms then re-query — Android tends to
      // surface the real state lazily.
      setTimeout(refresh, 600);
    }
  };

  return (
    <KvCard
      title="连通性 · Connectivity"
      icon={<Wifi className="h-4 w-4 text-accent" />}
      action={
        <button
          className="btn-ghost"
          onClick={refresh}
          disabled={!enabled || wifiQ.isFetching || airplaneQ.isFetching}
          title="刷新"
        >
          <RefreshCw
            className={clsx('h-3.5 w-3.5', (wifiQ.isFetching || airplaneQ.isFetching) && 'animate-spin')}
          />
        </button>
      }
    >
      <div className="space-y-3">
        {fatalErr && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[12px]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            <div className="min-w-0">
              <div className="font-medium text-amber-600">无法读取网络状态</div>
              <p className="mt-0.5 text-ink-muted break-words">{fatalErr.message}</p>
              {/^UNSUPPORTED_CMD/.test(fatalErr.message) && (
                <p className="mt-0.5 text-ink-muted">
                  服务端命令目录里没有这条命令。如果刚升级过 adh，请重新运行启动脚本（幂等替换会自动停旧拉新）：
                  <code className="ml-1 rounded bg-bg-soft px-1 font-mono text-[11px]">
                    yarn adh:restart
                  </code>
                </p>
              )}
            </div>
          </div>
        )}
        {actionErr && (
          <div className="rounded-md border border-rose-500/40 bg-rose-500/5 px-3 py-2 text-[12px] text-rose-500">
            {actionErr}
          </div>
        )}
        <WifiRow
          wifi={wifi}
          loading={wifiQ.isLoading}
          disabled={!enabled || wifiQ.isLoading}
          onToggle={(next) =>
            runToggle(
              next ? 'net.wifi.enable' : 'net.wifi.disable',
              () =>
                qc.setQueryData(['net.wifi.status', serial], {
                  result: { ...wifi, enabled: next },
                }),
              () => qc.setQueryData(['net.wifi.status', serial], { result: wifi })
            )
          }
        />
        <ToggleRow
          icon={<Smartphone className="h-4 w-4 text-ink-muted" />}
          label="移动数据 · Mobile data"
          // No reliable read — start unchecked and let user flip; the API
          // returns ok regardless. The "subtitle" doubles as a hint.
          enabled={false}
          showsState={false}
          disabled={!enabled}
          subtitle="点击按钮切换；svc data 在大多数机型无 root 即可"
          onToggle={(next) => runToggle(next ? 'net.mobile.enable' : 'net.mobile.disable', () => undefined, () => undefined)}
        />
        <ToggleRow
          icon={<Plane className="h-4 w-4 text-ink-muted" />}
          label="飞行模式 · Airplane"
          enabled={!!airplane?.on}
          disabled={!enabled || airplaneQ.isLoading}
          subtitle={
            airplane?.on
              ? '所有无线电关闭'
              : '部分机型只能改 settings 标志位，需要用户手动确认弹框'
          }
          onToggle={(next) =>
            runToggle(
              `net.airplane.set`,
              () => qc.setQueryData(['net.airplane.get', serial], { result: { on: next } }),
              () => qc.setQueryData(['net.airplane.get', serial], { result: airplane })
            )
          }
          // Override: airplane.set takes a body, not a bare command name.
          customRun={async (next) => {
            if (!serial) return;
            try {
              qc.setQueryData(['net.airplane.get', serial], { result: { on: next } });
              await adbApi.run({ serial, name: 'net.airplane.set', args: { on: next } });
            } catch {
              qc.setQueryData(['net.airplane.get', serial], { result: airplane });
            } finally {
              setTimeout(refresh, 600);
            }
          }}
        />
      </div>
    </KvCard>
  );
}

/**
 * WiFi gets a wider, two-tier layout instead of `ToggleRow` because:
 *   - SSIDs can be long (`jiayou-zt1-US`, corp 5GHz names with Unicode emoji,
 *     ...) and `truncate` was eating them.
 *   - Operators want to see at a glance which network the device is on
 *     (especially when troubleshooting "why isn't my Charles capturing
 *     traffic" — wrong WiFi == wrong subnet == proxy is unreachable).
 *
 * Layout:
 *   ┌───────────────────────────────────────────────┐
 *   │ icon   WiFi                          [ON]  ⬤  │
 *   │       jiayou-zt1-US (font-medium, multi-line) │
 *   │       -34 dBm · 78 Mbps · 2.4G · 192.168.50.x │
 *   └───────────────────────────────────────────────┘
 */
function WifiRow({
  wifi,
  loading,
  disabled,
  onToggle,
}: {
  wifi?: WifiStatus;
  loading: boolean;
  disabled?: boolean;
  onToggle: (next: boolean) => void | Promise<void>;
}) {
  const enabled = !!wifi?.enabled;
  const ssid = wifi?.ssid;
  return (
    <div className="rounded-md bg-bg-soft/40 px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-bg-card">
            {enabled ? (
              <Wifi className="h-4 w-4 text-accent" />
            ) : (
              <WifiOff className="h-4 w-4 text-ink-dim" />
            )}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink">WiFi</div>
            {!enabled && (
              <div className="text-[11px] leading-snug text-ink-muted">
                {loading ? '加载中...' : '已关闭'}
              </div>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span
            className={clsx(
              'badge',
              enabled ? 'border-accent/40 bg-accent-soft text-accent' : ''
            )}
          >
            {enabled ? 'ON' : 'OFF'}
          </span>
          <Switch checked={enabled} disabled={disabled} onChange={onToggle} />
        </div>
      </div>
      {enabled && (
        // Indented under the icon column so the readout visually associates
        // with WiFi without needing the full row to grow.
        <div className="mt-1 pl-[38px]">
          {ssid ? (
            <div className="break-all text-sm font-medium leading-snug text-ink">{ssid}</div>
          ) : (
            <div className="text-sm font-medium leading-snug text-ink-muted">
              已连接（SSID 不可读）
            </div>
          )}
          <WifiMetrics wifi={wifi} />
        </div>
      )}
    </div>
  );
}

function WifiMetrics({ wifi }: { wifi?: WifiStatus }) {
  if (!wifi) return null;
  const tags: Array<{ key: string; label: string; tone?: 'accent' | 'muted' }> = [];
  if (wifi.rssi !== undefined) {
    // Rough RSSI buckets: above -55 = excellent (accent), -55..-70 = good
    // (muted), below -70 = weak (also muted but caller can colour later).
    tags.push({
      key: 'rssi',
      label: `${wifi.rssi} dBm`,
      tone: wifi.rssi >= -55 ? 'accent' : 'muted',
    });
  }
  if (wifi.linkSpeedMbps !== undefined) {
    tags.push({ key: 'link', label: `${wifi.linkSpeedMbps} Mbps`, tone: 'muted' });
  }
  if (wifi.frequencyMhz !== undefined) {
    tags.push({
      key: 'band',
      label: wifi.frequencyMhz >= 5000 ? '5G' : '2.4G',
      tone: 'muted',
    });
  }
  if (wifi.ipAddress) {
    tags.push({ key: 'ip', label: wifi.ipAddress, tone: 'muted' });
  }
  if (tags.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1.5">
      {tags.map((t) => (
        <span
          key={t.key}
          className={clsx(
            'rounded px-1.5 py-0.5 text-[11px] font-mono leading-tight',
            t.tone === 'accent'
              ? 'bg-accent-soft text-accent'
              : 'bg-bg-card text-ink-muted'
          )}
        >
          {t.label}
        </span>
      ))}
    </div>
  );
}

interface ToggleRowProps {
  icon: React.ReactNode;
  label: string;
  enabled: boolean;
  disabled?: boolean;
  subtitle?: string;
  showsState?: boolean;
  onToggle: (next: boolean) => void | Promise<void>;
  /** Replaces `onToggle` (which expects an inferred-from-cmd helper) when an arg payload is needed. */
  customRun?: (next: boolean) => Promise<void>;
}

function ToggleRow({
  icon,
  label,
  enabled,
  disabled,
  subtitle,
  showsState = true,
  onToggle,
  customRun,
}: ToggleRowProps) {
  const handle = async (next: boolean): Promise<void> => {
    if (customRun) await customRun(next);
    else await onToggle(next);
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-md bg-bg-soft/40 px-3 py-2">
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-bg-card">
          {icon}
        </span>
        <div className="min-w-0">
          <div className="text-sm font-medium text-ink">{label}</div>
          {subtitle && (
            <div className="text-[11px] leading-snug text-ink-muted break-words">{subtitle}</div>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {showsState && (
          <span
            className={clsx(
              'badge',
              enabled ? 'border-accent/40 bg-accent-soft text-accent' : ''
            )}
          >
            {enabled ? 'ON' : 'OFF'}
          </span>
        )}
        <Switch checked={enabled} disabled={disabled} onChange={handle} />
      </div>
    </div>
  );
}

/**
 * Minimal accessible toggle. We avoid `<input type="checkbox">` so the
 * styling stays consistent with the rest of the chrome.
 */
function Switch({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative h-6 w-10 rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed',
        checked ? 'bg-accent' : 'bg-bg-soft border border-line'
      )}
    >
      <span
        className={clsx(
          'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
          checked && 'translate-x-4'
        )}
      />
    </button>
  );
}
